import { MsgAction } from '../actions';
import { ApiCaller } from '../../config';
import { put } from 'redux-saga/effects';
import { Utils } from '../../config';
import { clearCachedFCMToken, readCachedFCMToken } from '../../config/firebase/firebase';
import { DELETE_SCOPE, PAGE_SIZE, ROSTER_PAGE_SIZE } from '../../config/constants';

/**
 * Query string builder that drops empty parameters.
 *
 * The page reads used to interpolate their parameters straight into the path, which is
 * how `?limit=${10}` came to be hard-coded in four separate sagas while
 * `config/constants` declared a `PAGE_SIZE` none of them used, and how an unfiltered
 * list read still sent `?search=`. It also escapes the value — an interpolated search
 * term never was.
 */
const queryString = (params) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    query.set(key, String(value));
  }
  const serialised = query.toString();
  return serialised ? `?${serialised}` : '';
};

export default class MsgMiddleware {
  static *CreateChatChannel(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post('/api/v1/chat/create-channel', payload);
      if (res.status == 200) {
        yield put(MsgAction.CreateChatChannelSuccess(res?.data));
        // Utils.showMessage('success', res?.data?.message ? res?.data?.message : "Chat Created Successfully")
        // Utils.showMessage('success', res?.data?.message ? res?.data?.message : "Chat Created Successfully")
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.CreateChatChannelFailure());
        Utils.showMessage(
          'error',
          res?.data?.message ? res?.data?.message : 'Could not start the chat'
        );
      }
    } catch {
      yield put(MsgAction.CreateChatChannelFailure());
      Utils.showMessage('error', 'Could not start the chat');
    }
  }

  static *ShareMedia(params) {
    const { payload } = params;

    try {
      let res = yield ApiCaller.Get(
        `/api/v1/chat/shared-media/${payload?.channel_id}${queryString({
          limit: PAGE_SIZE,
          offset: payload?.shareMediaOffset,
        })}`
      );
      if (res.status == 200) {
        let shareMediaData = res?.data?.data?.rows;
        yield put(
          MsgAction.ShareMediaSuccess({
            shareMediaData,
            shareMediaCount: res?.data?.data?.count,
            shareMediaLength: res?.data?.data?.rows?.length,
            // The reducer appends pages. It has to know which page this was, or
            // re-requesting the first one duplicates everything already held.
            shareMediaOffset: Number(payload?.shareMediaOffset) || 0,
          })
        );
        // Utils.showMessage('success', res?.data?.message ? res?.data?.message : "Chat Created Successfully")
        // cb && cb(res?.data)
      } else {
        yield put(MsgAction.ShareMediaFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Chat Not Created")
      }
    } catch {
      yield put(MsgAction.ShareMediaFailure());
      // Utils.showMessage('error', "Chat Not Created")
    }
  }

  static *CreateGroup(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post('/api/v1/chat/group', payload);
      if (res.status == 200) {
        yield put(MsgAction.CreateGroupSuccess(res?.data));
        cb && cb(res?.data);
        Utils.showMessage(
          'success',
          res?.data?.message ? res?.data?.message : 'Group Created successfully'
        );
      } else {
        yield put(MsgAction.CreateGroupFailure());
      }
    } catch {
      yield put(MsgAction.CreateGroupFailure());
    }
  }

  /**
   * Login-ID lookup for the group-create and add-people pickers.
   *
   * Silent by design. This runs debounced on every keystroke of a typeahead, so the
   * "User Found" / "User Not Found" toasts it used to raise fired once per search —
   * a stack of notifications for a query the user was still typing. A hit renders the
   * row; a miss clears `search_by_id` and the picker shows its own empty state. Neither
   * outcome is an event worth interrupting for, and a miss is not an error at all.
   */
  static *CreateGroupAddId(params) {
    const { payload, cb } = params;
    try {
      if (payload.length == 9) {
        let res = yield ApiCaller.Get(`/api/v1/chat/user-search/${payload}`);
        if (res.data.data !== null) {
          yield put(MsgAction.CreateGroupAddIdSuccess(res?.data));
          cb && cb(res?.data);
        } else {
          yield put(MsgAction.CreateGroupAddIdFailure());
        }
      }
    } catch {
      yield put(MsgAction.CreateGroupAddIdFailure());
    }
  }

  static *GroupMembers(params) {
    const { payload } = params;

    try {
      let res = yield ApiCaller.Get(`/api/v1/chat/group/${payload}`);
      if (res.status == 200) {
        yield put(MsgAction.GroupMembersSuccess(res?.data));
      } else {
        yield put(MsgAction.GroupMembersFailure());
      }
    } catch {
      yield put(MsgAction.GroupMembersFailure());
    }
  }

  static *UpdateCreateGroup(params) {
    const { payload, cb } = params;

    try {
      let res = yield ApiCaller.Patch(`/api/v1/chat/group/${payload?.id}`, payload?.formData);
      if (res.status == 200) {
        yield put(MsgAction.UpdateCreateGroupSuccess(res?.data));
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.UpdateCreateGroupFailure());
      }
    } catch {
      yield put(MsgAction.UpdateCreateGroupFailure());
    }
  }

  /**
   * One page of a conversation's history.
   *
   * Paged by CURSOR, not by offset. The server orders `createdAt DESC` and an offset
   * counts backwards from the newest message, so a message arriving between two page
   * reads shifted the whole window: the client re-received a row it already held AND
   * never received the one that fell past the boundary. The duplicate was visible; the
   * hole was silent and permanent — scrolling further could not recover it, because the
   * offset window had already moved past it.
   *
   * `cursor` is opaque and comes straight back from `next_cursor`. Null means "the
   * newest page"; a null `next_cursor` in the response means there is nothing older.
   */
  static *MessageChatChannel(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(
        `/api/v1/chat/channel/${payload?.id}${queryString({
          limit: PAGE_SIZE,
          cursor: payload?.cursor,
        })}`
      );
      if (res.status == 200) {
        if (payload?.fromApi == true) {
          let reversedArr = res?.data?.data?.rows;
          // let reversedArr = [...arr].reverse(); // Create a new array and reverse it
          yield put(
            MsgAction.MessageChatChannelSuccess({
              reversedArr,
              count: res?.data?.data?.count,
              length: res?.data?.data?.rows?.length,
              nextCursor: res?.data?.data?.next_cursor ?? null,
              // When every other member had read everything up to. The durable half of
              // the read receipt — without it the sender's double ticks only ever
              // existed for as long as the pane stayed mounted. Keyed by the channel it
              // was asked for, because the group pane reads a group and a thread and
              // both land in the same store.
              othersReadAt: res?.data?.data?.others_read_at ?? null,
              channelId: payload?.id ?? null,
              // No cursor was sent, so this is the newest page — it REPLACES rather
              // than prepends. Without this, reopening a conversation stacked page one
              // on top of the history already held.
              replace: !payload?.cursor,
              fromApi: true,
            })
          );
        }
        // Utils.showMessage('success', res?.data?.message ? res?.data?.message : "Group Created successfully")
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.MessageChatChannelFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.MessageChatChannelFailure());
      // Utils.showMessage('error', "Group Not Created")
    }
  }

  static *UploadChatMedia(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/media`, payload);
      if (res.status == 200) {
        yield put(MsgAction.UploadChatMediaSuccess(res?.data));
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.UploadChatMediaFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.UploadChatMediaFailure());
      // Utils.showMessage('error', "Group Not Created")
    }
  }

  static *UnreadMessageDot() {
    // const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(`/api/v1/chat/unread-messages`);
      if (res.status == 200) {
        yield put(MsgAction.UnreadMessageDotSuccess(res?.data));
        // Utils.showMessage('success', res?.data?.message ? res?.data?.message : "Group Created successfully")
        // cb && cb()
      } else {
        yield put(MsgAction.UnreadMessageDotFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.UnreadMessageDotFailure());
      // Utils.showMessage('error', "Group Not Created")
    }
  }

  static *DeleteMessage(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Delete(`/api/v1/chat/message/${payload}`);
      if (res.status == 200) {
        // The bubble disappearing is the confirmation; only the failure needs saying.
        yield put(MsgAction.DeleteMessageSuccess(res?.data));
        cb && cb();
      } else {
        yield put(MsgAction.DeleteMessageFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.DeleteMessageFailure());
      Utils.showMessage('error', 'Message not deleted');
    }
  }

  static *AddUsersGroup(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/group/add-users`, payload);
      if (res.status == 200) {
        yield put(MsgAction.AddUsersGroupSuccess(res?.data));
        cb && cb(res);
        // Utils.showMessage('success', res?.data?.message ? res?.data?.message : "Add People successfully")
      } else {
        yield put(MsgAction.AddUsersGroupFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.AddUsersGroupFailure());
      // Utils.showMessage('error', "Group Not Created")
    }
  }

  /**
   * The conversation list, and the search over it.
   *
   * This asked for EVERY channel the user belonged to, in one request, with no limit —
   * and the sidebar re-issued it on every inbound socket message, every read receipt and
   * every delete. Now one page of 20, cursor-paged on scroll.
   *
   * `payload` used to be the bare search string. It is an object now; a string is still
   * accepted so nothing that calls this with one breaks mid-migration.
   */
  static *GlobalSearchChatsGroups(params) {
    const { payload, cb } = params;
    const options = typeof payload === 'string' || payload == null ? { search: payload } : payload;
    const cursor = options?.cursor ?? null;

    try {
      const res = yield ApiCaller.Get(
        `/api/v1/chat/global-search${queryString({
          search: options?.search,
          limit: PAGE_SIZE,
          cursor,
        })}`
      );
      if (res.status == 200) {
        yield put(
          MsgAction.GlobalSearchChatsGroupsSuccess({
            ...res?.data,
            nextCursor: res?.data?.data?.next_cursor ?? null,
            // No cursor means a first page — a fresh list, a new search term, or a
            // refresh after something was created. Anything else extends the list.
            replace: !cursor,
          })
        );
        cb && cb();
      } else {
        yield put(MsgAction.GlobalSearchChatsGroupsFailure());
      }
    } catch {
      yield put(MsgAction.GlobalSearchChatsGroupsFailure());
    }
  }

  /**
   * One page of a channel's roster, for the details panel.
   *
   * `GET /chat/group/:id` returns the channel plus a bounded roster in one payload, and
   * the panel rendered that as though it were the whole membership — so member 201 of a
   * large channel was invisible, with nothing on screen saying so.
   */
  static *ChannelMembersPage(params) {
    const { payload, cb } = params;
    const offset = Number(payload?.offset) || 0;
    try {
      const res = yield ApiCaller.Get(
        `/api/v1/chat/group/${payload?.channel_id}/members${queryString({
          limit: payload?.limit ?? ROSTER_PAGE_SIZE,
          offset,
        })}`
      );
      if (res.status == 200) {
        yield put(
          MsgAction.ChannelMembersPageSuccess({
            channel_id: payload?.channel_id,
            rows: res?.data?.data?.rows ?? [],
            count: res?.data?.data?.count ?? 0,
            replace: offset === 0,
          })
        );
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.ChannelMembersPageFailure());
      }
    } catch {
      yield put(MsgAction.ChannelMembersPageFailure());
    }
  }

  static *ReportMsg(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/message/report/${payload?.id}`, {
        description: payload?.description,
        type: payload?.type,
      });
      if (res.status == 200) {
        // Kept: a report produces no visible change, so this is the only
        // acknowledgement the reporter gets. It was raised twice — two identical
        // toasts stacked on every report — from a duplicated call.
        yield put(MsgAction.ReportMsgSuccess(res?.data));
        Utils.showMessage(
          'success',
          payload?.type == 'USER' ? 'User reported' : 'Message reported'
        );
        cb && cb();
      } else {
        yield put(MsgAction.ReportMsgFailure());
      }
    } catch {
      yield put(MsgAction.ReportMsgFailure());
    }
  }

  //Call
  static *StartCall(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post('/api/v1/chat/call', payload);
      if (res.status == 200) {
        yield put(MsgAction.StartCallSuccess(res?.data?.data));
        // The created call is handed to the callback because the caller has to
        // navigate to `/calling/:id` and this is the only place the new id exists.
        // It used to be called with no arguments at all.
        cb && cb(res?.data?.data);
      } else {
        yield put(MsgAction.StartCallFailure());
      }
    } catch {
      yield put(MsgAction.StartCallFailure());
    }
  }

  static *JoinCall(params) {
    const { payload } = params;
    try {
      let res = yield ApiCaller.Patch(`/api/v1/chat/call/${payload?.id}`, {
        status: payload?.status,
        isSecret: payload?.isSecret,
        /**
         * Which device is acting.
         *
         * A call rings every device the user is signed in on, and answering or
         * declining now calls the ring off on the others. That notice has to skip
         * the device that did the answering, or it would dismiss the call it is
         * sitting in — and push is addressed to devices by FCM token, so this is
         * the only thing that identifies "me" to the server.
         *
         * Attached here rather than at each call site: it describes the client,
         * not the call, so no caller should have to remember it.
         */
        device_token: readCachedFCMToken(),
      });
      if (res.status == 200) {
        yield put(MsgAction.JoinCallSuccess(res?.data));
        // cb && cb(res?.data)
      } else {
        yield put(MsgAction.JoinCallFailure());
      }
    } catch {
      yield put(MsgAction.JoinCallFailure());
    }
  }

  static *RecentCallHistory(params) {
    const { payload, cb } = params;
    try {
      const offset = Number(payload) || 0;
      let res = yield ApiCaller.Get(
        `/api/v1/chat/call/history${queryString({
          limit: PAGE_SIZE,
          offset,
          call_status: 'RECENT',
        })}`
      );
      if (res.status == 200) {
        yield put(
          MsgAction.RecentCallHistorySuccess({
            CallsData: res?.data?.data?.calls,
            countCalls: res?.data?.data?.count,
            // Offset 0 is a fresh first page. Appending it unconditionally stacked a
            // second copy of page one onto the list whenever the tab was re-entered.
            replace: offset === 0,
          })
        );
        cb && cb();
      } else {
        yield put(MsgAction.RecentCallHistoryFailure());
      }
    } catch {
      yield put(MsgAction.RecentCallHistoryFailure());
    }
  }
  static *MissedCallHistory(params) {
    const { payload } = params;
    try {
      const offset = Number(payload) || 0;
      let res = yield ApiCaller.Get(
        `/api/v1/chat/call/history${queryString({
          limit: PAGE_SIZE,
          offset,
          call_status: 'MISSED',
        })}`
      );
      if (res.status == 200) {
        yield put(
          MsgAction.MissedCallHistorySuccess({
            CallsDataMissed: res?.data?.data?.calls,
            countCallsMissed: res?.data?.data?.count,
            replace: offset === 0,
          })
        );
      } else {
        yield put(MsgAction.MissedCallHistoryFailure());
      }
    } catch {
      yield put(MsgAction.MissedCallHistoryFailure());
    }
  }

  static *UsersData(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/call/users`, payload);
      if (res.status == 200) {
        yield put(MsgAction.UsersDataSuccess(res?.data));
        cb && cb(res?.data?.data);
      } else {
        yield put(MsgAction.UsersDataFailure());
      }
    } catch {
      yield put(MsgAction.UsersDataFailure());
    }
  }

  static *UserProfileData(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(`/api/v1/user/profile/${payload}`);
      if (res.status == 200) {
        yield put(MsgAction.UserProfileDataSuccess(res?.data));
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.UserProfileDataFailure());
      }
    } catch {
      yield put(MsgAction.UserProfileDataFailure());
    }
  }

  /**
   * Callback-only: the result is a decision, not screen state, and keeping it in
   * the store would leave a stale "this call is alive" answer lying around for
   * the next call to read.
   *
   * `cb(null)` on any failure — including a 404 for a call the server has no
   * record of — so the caller can tell "gone" from "answered".
   */
  static *CallDetails(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(`/api/v1/chat/call/${payload}`);
      cb && cb(res.status == 200 ? res?.data?.data : null);
    } catch {
      cb && cb(null);
    }
  }

  static *ActiveGroupCall(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(`/api/v1/chat/call/active/${payload}`);
      cb && cb(res.status == 200 ? (res?.data?.data ?? null) : null);
    } catch {
      // A group whose call state cannot be read is treated as not on a call: offering a
      // "Join" that fails is worse than not offering one.
      cb && cb(null);
    }
  }

  /**
   * Everything needed to be on a call, from its id alone — the token included.
   *
   * This is what a tab arriving at `/calling/:id` with nothing in memory calls. Like
   * `CallDetails` it reports failure as `null` rather than throwing: the resume path
   * treats "cannot tell" as "gone", because rejoining a dead call shows a convincing
   * lie whereas refusing to rejoin a live one costs a redial.
   */
  static *CallSession(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(`/api/v1/chat/call/${payload}/session`);
      cb && cb(res.status == 200 ? res?.data?.data : null);
    } catch {
      cb && cb(null);
    }
  }

  static *MultipleCallUsersData(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(`/api/v1/chat/call/users/${payload}`);
      if (res.status == 200) {
        yield put(MsgAction.MultipleCallUsersDataSuccess(res?.data));
        cb && cb(res?.data?.data);
      } else {
        yield put(MsgAction.MultipleCallUsersDataFailure());
      }
    } catch {
      yield put(MsgAction.MultipleCallUsersDataFailure());
    }
  }

  // Threads
  static *CreateThreads(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post('/api/v1/chat/create-thread', payload);
      if (res.status == 200) {
        yield put(MsgAction.CreateThreadsSuccess(res?.data));
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.CreateThreadsFailure());
      }
    } catch {
      yield put(MsgAction.CreateThreadsFailure());
    }
  }

  static *JoinThreads(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/join-thread/${payload}`, {
        type: 'THREAD',
      });
      if (res.status == 200) {
        // The thread panel opening is the confirmation.
        yield put(MsgAction.JoinThreadsSuccess(res?.data?.data));
        cb && cb();
      } else {
        yield put(MsgAction.JoinThreadsFailure());
      }
    } catch {
      yield put(MsgAction.JoinThreadsFailure());
    }
  }

  static *ThreadsDetails(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(`/api/v1/chat/thread/${payload}`);
      if (res.status == 200) {
        yield put(MsgAction.ThreadsDetailsSuccess(res?.data?.data));
        cb && cb(res?.data?.data);
      } else {
        yield put(MsgAction.ThreadsDetailsFailure());
      }
    } catch {
      yield put(MsgAction.ThreadsDetailsFailure());
    }
  }

  static *ThreadsListing(params) {
    const { payload, cb } = params;
    try {
      // Was `limit=10000000000000000` — 1e16, past the 32-bit range the ORM's `take` is
      // defined over, and the server used it verbatim. The huge limit existed because
      // this endpoint pages over GROUPS and the caller wanted one specific group's
      // threads; `group_id` asks for that directly instead.
      const options =
        typeof payload === 'object' && payload !== null ? payload : { offset: payload };
      const offset = Number(options?.offset) || 0;
      let res = yield ApiCaller.Get(
        `/api/v1/chat/threads${queryString({
          limit: PAGE_SIZE,
          offset,
          group_id: options?.group_id,
        })}`
      );

      if (res.status == 200) {
        yield put(
          MsgAction.ThreadsListingSuccess({
            ThreadListingData: res?.data?.data?.rows,
            ThreadListingCount: res?.data?.data?.count,
            replace: offset === 0,
          })
        );
        cb && cb();
      } else {
        yield put(MsgAction.ThreadsListingFailure());
      }
    } catch {
      yield put(MsgAction.ThreadsListingFailure());
    }
  }

  static *UploadThreadMedia(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/media`, payload);
      if (res.status == 200) {
        yield put(MsgAction.UploadThreadMediaSuccess(res?.data));
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.UploadThreadMediaFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.UploadThreadMediaFailure());
      // Utils.showMessage('error', "Group Not Created")
    }
  }

  static *ThreadChatChannel(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Get(
        `/api/v1/chat/channel/${payload?.id}${queryString({
          limit: PAGE_SIZE,
          cursor: payload?.cursor,
        })}`
      );
      if (res.status == 200) {
        if (payload?.fromApi == true) {
          let threadArr = res?.data?.data?.rows;
          // let reversedArr = [...arr].reverse(); // Create a new array and reverse it
          yield put(
            MsgAction.ThreadChatChannelSuccess({
              threadArr,
              countThread: res?.data?.data?.count,
              lengthThread: res?.data?.data?.rows?.length,
              nextCursor: res?.data?.data?.next_cursor ?? null,
              replace: !payload?.cursor,
              // A thread is a channel with its own membership, so it has its own
              // read-by-everyone aggregate — keyed by its own id, not the parent group's.
              othersReadAt: res?.data?.data?.others_read_at ?? null,
              channelId: payload?.id ?? null,
              fromApi: true,
            })
          );
        }
        // Utils.showMessage('success', res?.data?.message ? res?.data?.message : "Group Created successfully")
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.ThreadChatChannelFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.ThreadChatChannelFailure());
      // Utils.showMessage('error', "Group Not Created")
    }
  }

  static *DeleteUserProfile(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Delete(`/api/v1/user/${payload}`);
      if (res.status == 200) {
        yield put(MsgAction.DeleteUserProfileSuccess());
        // The account row is gone, so its user_devices rows cascade-delete
        // server-side; just drop the local token cache.
        clearCachedFCMToken();
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        Utils.showMessage('success', 'Your account has been deleted');
        cb && cb();
      } else {
        yield put(MsgAction.DeleteUserProfileFailure());
        Utils.showMessage('error', 'Your account could not be deleted');
      }
    } catch {
      yield put(MsgAction.DeleteUserProfileFailure());
      Utils.showMessage('error', 'Your account could not be deleted');
    }
  }

  static *NotificationsLists(params) {
    const { payload, cb } = params;
    const offset = Number(payload?.offset) || 0;
    const kind = payload?.kind || 'all';
    // Server-side for the same reason `kind` is: only one page is in the client's
    // hands, so filtering it locally searches a fraction of the feed and presents the
    // result as all of it.
    const search = payload?.search;
    try {
      let res = yield ApiCaller.Get(
        `/api/v1/user/notifications${queryString({
          limit: PAGE_SIZE,
          offset,
          kind,
          search,
        })}`
      );
      if (res.status == 200) {
        yield put(
          MsgAction.NotificationsListsSuccess({
            notificationListRows: res?.data?.data?.rows,
            notificationListCount: res?.data?.data?.count,
            // Echoed by the server, so the reducer records which tab the rows
            // belong to rather than trusting the request that is now in the past.
            notificationListKind: res?.data?.data?.kind || kind,
            // Offset 0 is a fresh first page, not more of the current one. The
            // reducer used to append unconditionally, so remounting the panel
            // stacked page 1 onto the list it already held — duplicate rows and
            // duplicate React keys.
            replace: offset === 0,
          })
        );
        cb && cb();
      } else {
        yield put(MsgAction.NotificationsListsFailure());
      }
    } catch {
      yield put(MsgAction.NotificationsListsFailure());
    }
  }

  static *NotificationsUnreadCount(params) {
    const { cb } = params;
    try {
      const res = yield ApiCaller.Get('/api/v1/user/notifications/unread-count');
      if (res.status == 200) {
        yield put(MsgAction.NotificationsUnreadCountSuccess(res?.data?.data || {}));
        cb && cb();
      } else {
        yield put(MsgAction.NotificationsUnreadCountFailure());
      }
    } catch {
      yield put(MsgAction.NotificationsUnreadCountFailure());
    }
  }

  static *MarkNotificationsRead(params) {
    const { payload, cb } = params;
    const ids = Array.isArray(payload) && payload.length > 0 ? payload : null;
    try {
      const res = yield ApiCaller.Patch('/api/v1/user/notifications/read', ids ? { ids } : {});
      if (res.status == 200) {
        yield put(MsgAction.MarkNotificationsReadSuccess({ ids }));
        cb && cb();
      } else {
        yield put(MsgAction.MarkNotificationsReadFailure());
      }
    } catch {
      yield put(MsgAction.MarkNotificationsReadFailure());
    }
  }

  static *ToggleNotifications(params) {
    const { payload, cb, fcmToken } = params;
    try {
      // fcmToken is passed post-login so the backend can persist it (direct-auth
      // login no longer routes it through the backend). Omitted on plain toggles.
      let res = yield ApiCaller.Patch(`/api/v1/user/user-notification`, {
        status: payload,
        ...(fcmToken ? { fcm_token: fcmToken } : {}),
      });
      if (res.status == 200) {
        yield put(MsgAction.ToggleNotificationsSuccess(res?.data));

        cb && cb();
      } else {
        yield put(MsgAction.ToggleNotificationsFailure());
      }
    } catch {
      yield put(MsgAction.ToggleNotificationsFailure());
    }
  }

  static *EditUserProfile(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Patch(`/api/v1/user/editProfile/${payload?.id}`, payload?.formData);
      if (res.status == 200) {
        yield put(MsgAction.EditUserProfileSuccess(res?.data));
        cb && cb(res?.data?.data);
      } else {
        yield put(MsgAction.EditUserProfileFailure());
      }
    } catch {
      // Interceptor in config/api/index.js already shows the error toast
      yield put(MsgAction.EditUserProfileFailure());
    }
  }

  static *DeleteThread(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Delete(`/api/v1/chat/thread/${payload}`);
      if (res.status == 200) {
        // The thread is gone from the list; only the failure below needs saying.
        yield put(MsgAction.DeleteThreadSuccess(res?.data));
        cb && cb();
      } else {
        yield put(MsgAction.DeleteThreadFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.DeleteThreadFailure());
      Utils.showMessage('error', 'Thread not deleted');
    }
  }

  static *JoinGroup(params) {
    const { payload, cb } = params;

    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/group/join`, payload);
      if (res.status == 200) {
        yield put(MsgAction.JoinGroupSuccess(res?.data));
        cb && cb();
      } else {
        yield put(MsgAction.JoinGroupFailure());
        // Utils.showMessage('error', res?.data?.message ? res?.data?.message : "Group Not Created")
      }
    } catch {
      yield put(MsgAction.JoinGroupFailure());
      Utils.showMessage('error', 'Could not join the group');
    }
  }

  static *DeleteChannel(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Delete(`/api/v1/chat/channel/${payload}`);
      if (res.status == 200) {
        yield put(MsgAction.DeleteChannelSuccess(res?.data));
        yield put(MsgAction.GlobalSearchChatsGroups());
        cb && cb();
      } else {
        yield put(MsgAction.DeleteChannelFailure());
      }
    } catch {
      yield put(MsgAction.DeleteChannelFailure());
      Utils.showMessage('error', 'Chat not deleted');
    }
  }

  static *LeaveGroup(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/group/leave/${payload}`, {});
      if (res.status == 200) {
        // Leaving navigates to /messages and the group is gone from the sidebar —
        // a toast about it would land on a screen that no longer shows the group.
        yield put(MsgAction.LeaveGroupSuccess(res?.data));
        yield put(MsgAction.GlobalSearchChatsGroups());
        cb && cb();
      } else {
        yield put(MsgAction.LeaveGroupFailure());
      }
    } catch {
      yield put(MsgAction.LeaveGroupFailure());
      Utils.showMessage('error', 'Failed to leave group');
    }
  }

  // ── Channel administration ───────────────────────────────────────────────────
  // Each of these refreshes GroupMembers on success rather than patching local
  // state: the server decides `can_post`, `my_role` and which roster it will
  // disclose, so a re-read is the only way to stay in step with it.

  static *PromoteChannelAdmins(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/group/${payload?.channel_id}/admins`, {
        member_ids: payload?.member_ids,
      });
      if (res.status == 200) {
        yield put(MsgAction.PromoteChannelAdminsSuccess(res?.data));
        Utils.showMessage('success', res?.data?.message || 'Admins added successfully');
        yield put(MsgAction.GroupMembers(payload?.channel_id));
        cb && cb();
      } else {
        yield put(MsgAction.PromoteChannelAdminsFailure());
      }
    } catch {
      yield put(MsgAction.PromoteChannelAdminsFailure());
    }
  }

  static *DemoteChannelAdmin(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Delete(
        `/api/v1/chat/group/${payload?.channel_id}/admins/${payload?.member_id}`
      );
      if (res.status == 200) {
        yield put(MsgAction.DemoteChannelAdminSuccess(res?.data));
        Utils.showMessage('success', res?.data?.message || 'Admin removed successfully');
        yield put(MsgAction.GroupMembers(payload?.channel_id));
        cb && cb();
      } else {
        yield put(MsgAction.DemoteChannelAdminFailure());
      }
    } catch {
      yield put(MsgAction.DemoteChannelAdminFailure());
    }
  }

  static *TransferChannelOwnership(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Post(`/api/v1/chat/group/${payload?.channel_id}/transfer`, {
        member_id: payload?.member_id,
      });
      if (res.status == 200) {
        yield put(MsgAction.TransferChannelOwnershipSuccess(res?.data));
        Utils.showMessage('success', res?.data?.message || 'Ownership transferred');
        yield put(MsgAction.GroupMembers(payload?.channel_id));
        cb && cb();
      } else {
        yield put(MsgAction.TransferChannelOwnershipFailure());
      }
    } catch {
      yield put(MsgAction.TransferChannelOwnershipFailure());
    }
  }

  static *RemoveChannelMember(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Delete(
        `/api/v1/chat/group/${payload?.channel_id}/members/${payload?.member_id}`
      );
      if (res.status == 200) {
        yield put(MsgAction.RemoveChannelMemberSuccess(res?.data));
        Utils.showMessage('success', res?.data?.message || 'Member removed');
        yield put(MsgAction.GroupMembers(payload?.channel_id));
        cb && cb();
      } else {
        yield put(MsgAction.RemoveChannelMemberFailure());
      }
    } catch {
      yield put(MsgAction.RemoveChannelMemberFailure());
    }
  }

  /**
   * Withdraw a message from every member of a channel.
   *
   * The local stream is patched immediately on success rather than waiting for the
   * broadcast: the retracting admin's own socket is in the fan-out, but a dropped
   * event would otherwise leave the person who pressed the button looking at a
   * message they just deleted.
   */
  static *RetractMessage(params) {
    const { payload, cb } = params;
    try {
      let res = yield ApiCaller.Delete(
        `/api/v1/chat/message/${payload?.message_id}?scope=${DELETE_SCOPE.ALL}`
      );
      if (res.status == 200) {
        yield put(MsgAction.RetractMessageSuccess(res?.data));
        yield put(
          MsgAction.MessageDeletedForAll({
            message_id: payload?.message_id,
            deleted_by_id: payload?.deleted_by_id,
          })
        );
        Utils.showMessage('success', res?.data?.message || 'Message deleted for everyone');
        cb && cb();
      } else {
        yield put(MsgAction.RetractMessageFailure());
      }
    } catch {
      yield put(MsgAction.RetractMessageFailure());
    }
  }

  // ─── saved messages (bookmarks) ────────────────────────────────────────────

  /**
   * Star / unstar, optimistically.
   *
   * The star flips before the request goes out and flips back only if the server
   * refuses — a bookmark is a low-stakes toggle and waiting on a round trip to
   * redraw it makes the whole action feel broken.
   *
   * The failure path reverts to the state before the tap, not to the negation of the
   * desired one, so two rapid taps cannot leave the icon stuck showing something the
   * server never agreed to. The `error` toast is raised by the axios interceptor, so
   * nothing is shown here — but the cap refusal (409) is worth naming, since it is
   * the one failure the user can actually act on.
   */
  static *ToggleSaveMessage(params) {
    const { payload, cb } = params;
    const messageId = payload?.message_id;
    const desired = Boolean(payload?.is_saved);
    if (!messageId) return;

    yield put(MsgAction.MessageSaveState({ message_id: messageId, is_saved: desired }));

    try {
      const res = desired
        ? yield ApiCaller.Post(`/api/v1/chat/message/${messageId}/save`, {
            ...(payload?.note ? { note: payload.note } : {}),
          })
        : yield ApiCaller.Delete(`/api/v1/chat/message/${messageId}/save`);

      if (res.status == 200) {
        yield put(MsgAction.ToggleSaveMessageSuccess({ message_id: messageId, is_saved: desired }));
        cb && cb(res?.data);
      } else {
        yield put(
          MsgAction.ToggleSaveMessageFailure({ message_id: messageId, is_saved: !desired })
        );
      }
    } catch {
      yield put(MsgAction.ToggleSaveMessageFailure({ message_id: messageId, is_saved: !desired }));
    }
  }

  /**
   * The saved list. `offset: 0` replaces the list, anything higher appends — so the
   * same action serves both the initial load and infinite scroll, and the reducer
   * decides which without a second action type.
   */
  static *SavedMessagesList(params) {
    const { payload, cb } = params;
    const offset = Number(payload?.offset) || 0;
    try {
      const res = yield ApiCaller.Get(
        `/api/v1/chat/saved${queryString({
          limit: payload?.limit ?? PAGE_SIZE,
          offset,
          search: payload?.search,
        })}`
      );
      if (res.status == 200) {
        yield put(MsgAction.SavedMessagesListSuccess({ ...res?.data, offset }));
        cb && cb(res?.data);
      } else {
        yield put(MsgAction.SavedMessagesListFailure());
      }
    } catch {
      yield put(MsgAction.SavedMessagesListFailure());
    }
  }
}
